import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Star, Heart, ChevronLeft, ChevronRight, ThumbsUp, ChevronDown, Scissors, CalendarCheck } from "lucide-react";
import api from "../lib/api";
import BusinessCard from "../components/BusinessCard";
import SearchSuggestions from "../components/SearchSuggestions";
import { useFavorite } from "../lib/favorites";

function CategoryMoreMenu({ categories }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
      }
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 whitespace-nowrap transition-colors ${
          open ? "text-white" : "text-white/90 hover:text-white"
        }`}
      >
        More
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: coords.top, right: coords.right }}
            className="z-50 w-[22rem] max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-top-1 duration-150"
          >
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.35)]">
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  All categories
                </p>
              </div>
              <div className="grid grid-cols-2 gap-1 p-3 sm:grid-cols-3">
                {categories.map((name) => (
                  <Link
                    key={name}
                    to={`/search?category=${encodeURIComponent(name)}`}
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-primary/10 hover:text-primary"
                  >
                    {name}
                  </Link>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const FALLBACK_CATEGORIES = [
  "Hair salons", "Barbers", "Nail studios", "Makeup artists", "Massage",
  "Clinics", "Tutors", "Tailors", "Home cleaners", "Event decorators",
  "Car wash", "Mechanics", "Dentists", "Photographers", "Tattoo studios",
];

const RWANDA_CITIES = [
  "Kigali", "Musanze", "Huye", "Rubavu", "Muhanga",
  "Rwamagana", "Nyagatare", "Karongi", "Nyanza", "Kayonza",
  "Rusizi", "Ruhango", "Gicumbi", "Kibungo", "Byumba",
];

function CitySpecialistSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h2 className="text-center font-montserrat text-2xl font-extrabold text-slate-900 sm:text-3xl">
        Find your SHUWA specialist by city
      </h2>
      <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-3">
        {RWANDA_CITIES.map((city) => (
          <Link
            key={city}
            to={`/search?city=${encodeURIComponent(city)}`}
            className="flex items-center gap-2 text-slate-700 hover:text-primary"
          >
            <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
            <span>{city}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Links to the service and city pages that exist — the ones with shops on
// them (see ServiceLanding) — so visitors and search engines can reach them.
function BrowseServicesSection() {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/seo/pages").then(({ data }) => setPages(data || [])).catch(() => {});
  }, []);

  if (pages.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
      <h2 className="text-center font-montserrat text-2xl font-extrabold text-slate-900 sm:text-3xl">
        Browse by service
      </h2>
      <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-2">
        {pages.map((page) => (
          <Link
            key={page.path}
            to={page.path}
            className="rounded-full border border-border bg-white px-3.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-primary hover:text-primary"
          >
            {page.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

// Screenshots live in public/screenshots/. Make them with /screenshot-maker.html
// ("shuwa.app" tab) so they come out framed and sized consistently.
const HOW_IT_WORKS_STEPS = [
  {
    icon: MapPin,
    image: "/screenshots/home-1.jpg",
    alt: "SHUWA search results showing businesses in Kigali",
    title: "Discover",
    description: "Search for the salon, barber, clinic, or service you need, right in your area.",
  },
  {
    icon: Scissors,
    image: "/screenshots/home-2.jpg",
    alt: "A business page in SHUWA listing services and prices in RWF",
    title: "Pick a service",
    description: "Browse services, prices, and available times, then choose what works for you.",
  },
  {
    icon: CalendarCheck,
    image: "/screenshots/home-3.jpg",
    alt: "Picking a date and time for a booking in SHUWA",
    title: "Book instantly",
    description: "Pick a time, pay if you'd like, and get confirmed — no calls, no waiting.",
    cta: { to: "/search", label: "Search & filter" },
  },
];

function HowItWorksSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="mx-auto max-w-4xl text-center">
        <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
          How it works
        </span>
        <p className="mt-4 font-montserrat text-xl font-bold text-slate-900 sm:text-2xl">
          Skip the line, Book on
          <span className="mt-1 block text-3xl font-extrabold tracking-wide text-primary sm:text-4xl">
            SHUWA
          </span>
        </p>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600">
          Discover local businesses, choose a service, and book a time that
          works for you — all in one simple place.
        </p>
      </div>

      <div className="mt-14 grid gap-8 sm:grid-cols-3 sm:gap-6">
        {HOW_IT_WORKS_STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="flex flex-col items-center text-center">
              {/* Badges sit outside the clipped image so they aren't cut off. */}
              <div className="relative w-full">
                <div className="aspect-[16/10] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5">
                  <img src={step.image} alt={step.alt} width="960" height="600" className="h-full w-full object-cover" loading="lazy" />
                </div>
                <span className="absolute -top-3 left-4 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white ring-2 ring-white">
                  {i + 1}
                </span>
                <span className="absolute -bottom-4 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-primary text-white shadow ring-4 ring-white">
                  <Icon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                </span>
              </div>
              <h3 className="mt-8 font-montserrat text-lg font-bold text-slate-900">
                {step.title}
              </h3>
              <p className="mt-2 max-w-xs text-sm text-slate-600">{step.description}</p>
              {step.cta && (
                <Link
                  to={step.cta.to}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-white"
                >
                  <Search className="h-4 w-4" aria-hidden="true" />
                  {step.cta.label}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 flex justify-center">
        <Link
          to="/search"
          className="inline-block rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/20 hover:bg-primary/90"
        >
          Find a business near you
        </Link>
      </div>
    </section>
  );
}

// The heart on a shop's photo. It sits inside the card's link, so the click must not follow it.
function FavoriteHeart({ business }) {
  const [saved, toggle] = useFavorite(business);
  return (
    <button
      type="button"
      aria-label={saved ? "Remove from favorites" : "Save to favorites"}
      aria-pressed={saved}
      onClick={(e) => {
        e.preventDefault();
        toggle();
      }}
      className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-slate-500 shadow hover:text-primary"
    >
      <Heart className={`h-3.5 w-3.5 ${saved ? "fill-red-500 text-red-500" : ""}`} />
    </button>
  );
}

function RecommendedSection({ businesses }) {
  const [ref, setRef] = useState(null);

  const scrollBy = (dir) => {
    if (!ref) return;
    ref.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  if (!businesses?.length) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-montserrat text-xl font-bold text-slate-900 sm:text-2xl">
          Recommended
        </h2>
        <div className="hidden gap-2 sm:flex">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-slate-600 hover:border-primary hover:text-primary"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-slate-600 hover:border-primary hover:text-primary"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        ref={setRef}
        className="flex gap-4 overflow-x-auto pb-2 scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {businesses.map((biz) => {
          const image = biz.venue_photo_urls?.[0] || biz.logo_url;
          return (
            <Link
              key={biz.id}
              to={`/${biz.slug}`}
              state={{ source: "marketplace" }}
              className="group relative w-56 shrink-0 overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition hover:shadow-md sm:w-64"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                {image ? (
                  <img
                    src={image}
                    alt={biz.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-3xl font-montserrat font-bold text-primary/20">
                    {biz.name?.[0]}
                  </div>
                )}
                {biz.rating_count > 0 && (
                  <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-xs font-bold text-slate-900 shadow">
                    {Number(biz.rating_avg).toFixed(1)} <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  </span>
                )}
                <FavoriteHeart business={biz} />
              </div>
              <div className="p-3">
                <p className="line-clamp-1 text-sm font-semibold text-slate-900">{biz.name}</p>
                <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{biz.city || biz.address}</p>
                <p className="mt-1 text-xs text-slate-500">{biz.rating_count || 0} reviews</p>
                {biz.is_promoted && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                      <ThumbsUp className="h-3 w-3" /> Promoted
                    </span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api
      .get("/businesses", { params: { sort: "rating", per_page: 12 } })
      .then(({ data }) => setBusinesses(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/search?${params.toString()}`);
  };

  const categoryNames = categories.length ? categories.map((c) => c.name) : FALLBACK_CATEGORIES;

  const goToSearch = (params) => {
    setSuggestOpen(false);
    navigate(`/search?${params.toString()}`);
  };

  const onPickSuggestion = (item) => {
    if (item.type === "category") {
      goToSearch(new URLSearchParams({ category: item.value }));
    } else {
      setQ(item.value);
      goToSearch(new URLSearchParams({ q: item.value }));
    }
  };

  return (
    <div>
      <section className="relative isolate text-white">
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/videos/hero.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="absolute inset-0 bg-slate-900/55" />
        </div>

        <div className="relative mx-auto flex min-h-[360px] max-w-4xl flex-col items-center justify-center px-4 py-16 text-center sm:px-6 sm:py-20">
          <h1 className="font-montserrat text-4xl font-extrabold sm:text-6xl">
            Book with confidence
          </h1>
          <p className="mt-4 max-w-xl text-base text-white/90">
            Your appointment assured
          </p>

          <form
            onSubmit={onSearch}
            className="relative mt-8 flex w-full max-w-xl items-center gap-2 rounded-full bg-white px-5 py-3.5 shadow-xl"
          >
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              placeholder="Search services or businesses"
              className="w-full text-sm text-slate-900 outline-none"
            />
            <button type="submit" className="sr-only">
              Search
            </button>
            <SearchSuggestions
              query={q}
              open={suggestOpen}
              onClose={() => setSuggestOpen(false)}
              onPick={onPickSuggestion}
              categories={categoryNames}
            />
          </form>
        </div>

        <div className="relative mx-auto flex max-w-7xl items-center justify-center gap-x-8 gap-y-3 overflow-x-auto px-4 pb-6 text-sm [-ms-overflow-style:none] [scrollbar-width:none] sm:px-6 [&::-webkit-scrollbar]:hidden">
          {categoryNames.slice(0, 8).map((name) => (
            <Link
              key={name}
              to={`/search?category=${encodeURIComponent(name)}`}
              className="shrink-0 whitespace-nowrap text-white/90 hover:text-white"
            >
              {name}
            </Link>
          ))}
          {categoryNames.length > 8 && (
            <CategoryMoreMenu categories={categoryNames.slice(8)} />
          )}
        </div>
      </section>

      <HowItWorksSection />

      <RecommendedSection businesses={businesses} />

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 sm:grid-cols-2 sm:gap-16">
          <div>
            <h2 className="font-montserrat text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Your appointment, assured.
            </h2>
            <p className="mt-5 text-slate-600">
              After a fresh fade, new braids, a set of nails or an hour on the
              massage table? Whatever you have in mind, the right professional
              is closer than you think.
            </p>
            <p className="mt-4 text-slate-600">
              SHUWA brings the salons, barbers and wellness studios around you
              into one app. See who is available, choose a time that suits you
              and confirm in a few taps, day or night, without making a single
              call.
            </p>
            <p className="mt-4 font-semibold text-slate-900">
              Find trusted businesses near you and reserve your spot on SHUWA.
            </p>
          </div>
          <img
            src="/screenshots/home-4.jpg"
            alt="The SHUWA home screen with popular businesses near you"
            width="800"
            height="800"
            loading="lazy"
            className="mx-auto aspect-square w-full max-w-sm rounded-3xl object-cover"
          />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 sm:grid-cols-2 sm:gap-16">
          <img
            src="/screenshots/home-5.jpg"
            alt="Upcoming appointments in SHUWA with options to cancel or report running late"
            width="800"
            height="800"
            loading="lazy"
            className="order-2 mx-auto aspect-square w-full max-w-sm rounded-3xl object-cover sm:order-1"
          />
          <div className="order-1 sm:order-2">
            <h2 className="font-montserrat text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Plans change? SHUWA has you covered.
            </h2>
            <p className="mt-5 text-slate-600">
              Book, reschedule, or cancel appointments with ease. Get timely
              reminders and manage your bookings anytime, anywhere with SHUWA.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 sm:grid-cols-2 sm:gap-16">
          <div>
            <h2 className="font-montserrat text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Discover the best professionals near you
            </h2>
            <p className="mt-5 text-slate-600">
              Explore SHUWA to discover trusted beauty, wellness, and service
              professionals near you. Browse their profiles, explore their
              work, and read reviews to find the right fit.
            </p>
            <p className="mt-4 text-slate-600">
              Your next appointment is just a few taps away. With SHUWA,
              discovering and booking services is simple and convenient.
            </p>
          </div>
          <img
            src="/screenshots/home-6.jpg"
            alt="A business profile in SHUWA with its rating, team and amenities"
            width="800"
            height="800"
            loading="lazy"
            className="mx-auto aspect-square w-full max-w-sm rounded-3xl object-cover"
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="order-1 flex flex-col items-center rounded-3xl bg-slate-900 p-8 text-center text-white sm:items-start sm:text-left">
            <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-900">
              For businesses
            </span>
            <h3 className="mt-4 font-montserrat text-2xl font-bold">
              SHUWA for your business
            </h3>
            <p className="mt-2 text-white/80">
              The same app that brings you clients also runs your business —{" "}
              <span className="font-semibold text-white">Calendar, Booking, and
              Payments</span> all in one place. Customers can browse and see
              your services without an account — they only sign in when
              they're ready to confirm a booking.
            </p>
            <Link
              to="/for-business"
              className="mt-6 inline-block rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-white/90"
            >
              Grow my business
            </Link>
            <div className="mt-8">
              <img
                src="/screenshots/home-7.png"
                alt="The SHUWA business calendar with the day's bookings"
                width="448"
                height="921"
                loading="lazy"
                className="mx-auto h-auto w-56 drop-shadow-2xl"
              />
            </div>
          </div>

          <div className="order-2 flex flex-col items-center rounded-3xl bg-primary/10 p-8 text-center sm:items-start sm:text-left">
            <span className="inline-block rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">
              For customers
            </span>
            <h3 className="mt-4 font-montserrat text-2xl font-bold text-slate-900">
              Find &amp; book an appointment
            </h3>
            <p className="mt-2 text-slate-600">
              Cut the phone tag. Browse nearby businesses and compare reviews
              — no account needed. Sign in only when you're ready to{" "}
              <span className="font-semibold text-slate-900">confirm your booking</span>.
            </p>
            <Link
              to="/appointment"
              className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90"
            >
              Find something near you
            </Link>
            <div className="mt-8">
              <img
                src="/screenshots/home-8.png"
                alt="A barbershop's page in SHUWA with its services and prices"
                width="448"
                height="921"
                loading="lazy"
                className="mx-auto h-auto w-56 drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <CitySpecialistSection />

      <BrowseServicesSection />

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <h2 className="mb-4 font-montserrat text-xl font-bold text-slate-900">
          Popular near you
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : businesses.length ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {businesses.map((b) => (
              <BusinessCard key={b.id} business={b} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">
            No businesses yet — once your first partners are approved, they'll show up here.
          </p>
        )}
      </section>
    </div>
  );
}
